import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, inject, input, OnDestroy, OnInit
} from '@angular/core';
import { CurrentProductService } from '@spartacus/storefront';
import { Subscription, filter } from 'rxjs';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';

import { ITableFeatures, SdTableFeaturesComponent } from '@seb/web-design-system/tables';
import {
  BrandsEnum, BrandThemesEnum, ComponentWrapperWidthEnum, IComponentWrapper, MarginEnum, SdComponentWrapperComponent,
  SizesEnum, TabGroupLayoutEnum
} from '@seb/web-design-system/core';

import { BrandCornerMapper, HeadingParagraphMapper, ProductMapper } from '@seb/web-common/core/mappers';
import { BaseStoreService } from '@seb/web-common/core/services';
import { LameConfig } from '@seb/web-common/core/models';

@Component({
  selector: 'wc-pdp-features',
  templateUrl: './pdp-features.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdComponentWrapperComponent,
    SdTableFeaturesComponent,
    TranslatePipe
  ]
})
export class PdpFeaturesComponent implements OnInit, OnDestroy, AfterViewInit {
  brand = input(BrandsEnum.default);
  pageTemplate = input('');
  isInsideSection = input(false);
  class = input('');

  private readonly currentProductService = inject(CurrentProductService);
  private readonly translateService = inject(TranslateService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly productMapper = inject(ProductMapper);
  private readonly baseStoreService = inject(BaseStoreService);
  private readonly headingParagraphMapper = inject(HeadingParagraphMapper);
  private readonly brandCornerMapper = inject(BrandCornerMapper);

  @HostBinding('class')
  get hostClasses(): string {
    return [this.class, this.isInsideSection() ? 'is-inside-section' : ''].filter(Boolean).join(' ');
  }

  agecLawProducts = [];
  substanceProducts = [];
  showFeaturesTab = false;
  lameConfig: LameConfig;
  selectedTabIndex = 0;
  wrapperContent: IComponentWrapper;
  features: ITableFeatures;
  featuresAgecLaw: ITableFeatures;
  featuresSubstance: ITableFeatures;
  agecLaw = {};
  activeAgecLaw = false;

  tabGroupLayoutEnum = TabGroupLayoutEnum;
  ComponentWrapperWidthEnum = ComponentWrapperWidthEnum;
  MarginEnum = MarginEnum;

  private subscriptions = new Subscription();

  ngOnInit(): void {
    this.lameConfig = this.brandCornerMapper.toWcConfig(this.brand())?.pdp?.comparator || {};
    const baseStore = this.baseStoreService.baseStore;

    this.subscriptions.add(
      this.currentProductService.getProduct().pipe(filter(Boolean))
        .subscribe((product) => {
          this.activeAgecLaw = baseStore?.activateAgecLaw;
          this.agecLaw = product?.agecLaw;
          this.agecLawProducts = !!baseStore?.activateAgecLaw && !!this.agecLaw ? this.mapAgecLawFeatures() : [];
          this.substanceProducts = !!baseStore?.activateAgecLaw && !!this.agecLaw ? this.mapSubstanceFeatures() : [];
          this.showFeaturesTab = (product.classifications || []).some(
            (classification) => classification.type !== 'USER_FRIENDLY'
          );

          this.features = {
            classifications: this.productMapper.classificationToProductClassification(product.classifications, true)
          };

          this.featuresAgecLaw = this.buildFeaturesAgecLaw();
          this.featuresSubstance = this.buildFeaturesSubstance();
          this.wrapperContent = this.buildWrapperContent();

          this.cdRef.markForCheck();
        })
    );
  }

  ngAfterViewInit() {
    this.selectedTabIndex = 0;
    this.cdRef.markForCheck();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get componentId(): string {
    const template = this.pageTemplate();
    const idNumber = this.isInsideSection() ? 0 : 1;
    return `PdpFeaturesComponent_${template.charAt(0).toUpperCase() + template.slice(1).toLowerCase()}Page_${idNumber}`;
  }

  private mapAgecLawFeatures(): any[] {
    const order = [
      'productMiniRecycledRate', 'productRecyclability',
      'packageMiniRecycledRate', 'packageRecyclability',
      'rareEarthMass', 'preciousMetalsMass'
    ];
    const recyclability = ['productRecyclability', 'packageRecyclability'];

    return order
      .filter((key) => key in this.agecLaw)
      .map((key) => ({
        description: this.translateService.instant(`product.attribute.${key}.name`),
        featureValues: [
          {
            value: recyclability.includes(key)
              ? this.translateService.instant(`product.attribute.${key}.${this.agecLaw[key]}.name`)
              : this.agecLaw[key]
          },
          {
            value: recyclability.includes(key)
              ? this.translateService.instant(`product.attribute.${key}.${this.agecLaw[key]}.description`)
              : '-'
          }
        ]
      }));
  }

  private mapSubstanceFeatures(): any[] {
    const substance = ['productSvhc', 'productHazardousSubstances', 'productEndocrineDisruptors'];
    return substance
      .filter((key) => key in this.agecLaw)
      .map((key) => ({
        description: this.translateService.instant(`product.attribute.${key}.name`),
        featureValues: [{ value: this.agecLaw[key] }]
      }));
  }

  private buildFeaturesAgecLaw(): ITableFeatures {
    return {
      title: this.translateService.instant('pdp.characteristicstable.tab.environnement.section1.title'),
      subtitle: this.translateService.instant('pdp.characteristicstable.tab.environnement.section1.subtitle'),
      columnsSize: [3, 2, 3],
      classifications: [{ features: this.agecLawProducts.length ? this.agecLawProducts : [{}] }]
    };
  }

  private buildFeaturesSubstance(): ITableFeatures {
    return {
      title: this.translateService.instant('pdp.characteristicstable.tab.environnement.section2.title'),
      columnsSize: [3, 4],
      classifications: [{ features: this.substanceProducts }],
      footNotes: [
        { note: this.translateService.instant('pdp.characteristicstable.tab.environnement.section2.reassurancephrase1') },
        { note: this.translateService.instant('pdp.characteristicstable.tab.environnement.section2.reassurancephrase2') }
      ]
    };
  }

  private buildWrapperContent(): IComponentWrapper {
    const config = this.brandCornerMapper.toWcConfig(this.brand())?.pdp?.comparator;
    const richParagraphData = {
      heading: {
        headline: !!this.features?.classifications?.length && !this.isInsideSection()
          ? this.translateService.instant('pdp.characteristicstable.tab.environnementalCharacteristics.name')
          : undefined,
        headlineLevel: 'H2'
      },
      titleTheme: config?.titleTheme || BrandThemesEnum.brand,
      isUnderlined: config?.titleUnderlined,
      size: SizesEnum.l
    };

    return {
      titleLevel: 2,
      links: [],
      footNotes: [],
      heading: this.headingParagraphMapper.toHeadingParagraph(richParagraphData)
    };
  }
}
